import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import { Field, FormikHelpers, FormikProps } from "formik";
import { every, filter, find, keys, toLower, words } from "lodash";
import moment from "moment";
import React, {
  Fragment,
  useCallback, useMemo,
  useRef,
  useState
} from "react";
import * as Yup from "yup";
import shallow from "zustand/shallow";
import AlertMessage from "../../components/AlertMessage";
import Checkbox from "../../components/forms/Checkbox";
import DateInput from "../../components/forms/DateInput";
import DropdownSearch from "../../components/forms/DropdownSearch";
import DropdownSelect, {
  DropdownSelectOption
} from "../../components/forms/DropdownSelect";
import FormProvider from "../../components/forms/FormProvider";
import HandleFieldChange from "../../components/forms/HandleFieldChange";
import { ApiResponse } from "../../utils/api";
import { locale } from "../../utils/dates";
import timezones from "../../utils/timezones";
import useCurrency, { CurrencyKey } from "../../utils/useCurrency";
import { useHeliumData } from "../heliumData";
import { getFormattedDate } from "../installs/InstallFormSidebar";
import { useToast } from "../toast";
import { useWorkspaces } from "../workspaces";
import {
  exportHotspotRewardsReportType,
  useHotspots
} from "./HotspotsProvider";

interface Props {}

const ExportHotspotRewardsSidebar: React.FC<Props> = () => {
  const dialogRef = useRef(null);
  const formRef = useRef<FormikProps<FormValues>>(null);
  const now = useRef(new Date());
  const { setModalWarningConfig } = useToast();
  const { blockchainHaltMoment } = useHeliumData();
  const [currencyOptions, getCurrencyTitle] = useCurrency(
    (state) => [state.currencyOptions, state.getCurrencyTitle],
    shallow
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { workspaces, currentWorkspaceId } = useWorkspaces();
  const {
    exportHotspotRewards,
    exportHotspotRewardsSidebarConfig,
    setExportHotspotRewardsSidebarConfig
  } = useHotspots();
  const [isValidMaxPeriodDays, setIsValidMaxPeriodDays] = useState(true);

  const closeSidebar = useCallback(() => {
    setExportHotspotRewardsSidebarConfig({ show: false, hotspotIds: [] });
    setErrorMessage(null);
    setIsValidMaxPeriodDays(true);
  }, [setExportHotspotRewardsSidebarConfig]);

  const { show, hotspotIds } = useMemo(
    () => exportHotspotRewardsSidebarConfig,
    [exportHotspotRewardsSidebarConfig]
  );

  const initialValues = useMemo(() => {
    const currentWorkspace = currentWorkspaceId
      ? workspaces[currentWorkspaceId]
      : undefined;

    return {
      start_date: moment(now.current)
        .local()
        .subtract(1, "month")
        .startOf("month")
        .toDate(),
      end_date: moment(now.current)
        .local()
        .subtract(1, "month")
        .endOf("month")
        .toDate(),
      currency: find(currencyOptions, { key: CurrencyKey.usd }),
      timezone: currentWorkspace?.timezone
    };
  }, [currencyOptions, currentWorkspaceId, workspaces]);

  const onSubmit = useCallback(
    async (
      { start_date, end_date, currency, timezone, koinlyIsDefault }: FormValues,
      formikHelpers: FormikHelpers<FormValues>
    ) => {
      setErrorMessage(null);

      try {
        const requestValues = {
          start_date: getFormattedDate(start_date),
          end_date: getFormattedDate(end_date),
          currency: currency.key,
          timezone,
          hotspotIds,
          reportType: koinlyIsDefault
            ? exportHotspotRewardsReportType.koinly
            : exportHotspotRewardsReportType.default
        };

        const response = await exportHotspotRewards(requestValues);

        const apiResponse: ApiResponse<[]> = await response.json();

        if (!apiResponse.status) {
          setErrorMessage("Something went wrong");
        }

        closeSidebar();

        if (apiResponse.status) {
          setModalWarningConfig({
            title: "Export hotspot rewards",
            message: apiResponse.message,
            withoutCancelButton: true,
            confirmButtonClassname: "bg-hotspotty-400 w-auto",
            confirmButtonText: "OK",
            open: true
          });
        }
      } catch (error: any) {
        setErrorMessage("Something went wrong");
      }

      formikHelpers.setSubmitting(false);
    },
    [closeSidebar, exportHotspotRewards, hotspotIds, setModalWarningConfig]
  );

  const getTimezones = useCallback((searchInput: string | undefined) => {
    const filteredTimezones = keys(timezones).filter((timezone) => {
      if (searchInput) {
        const searchInputWords = words(searchInput, /[^, ]+/g).map(toLower);

        return every(
          searchInputWords,
          (word) =>
            toLower(timezone).includes(word) ||
            toLower(timezones[timezone] || "").includes(word)
        );
      }
      return true;
    });

    return filteredTimezones;
  }, []);

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed inset-0 z-20 overflow-hidden"
        open={show}
        onClose={closeSidebar}
        initialFocus={dialogRef}
      >
        <div ref={dialogRef} className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

          <div className="fixed inset-y-0 pl-16 max-w-full right-0 flex">
            <div className="absolute inset-0" onClick={closeSidebar} />
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="relative w-screen max-w-full sm:max-w-md">
                <FormProvider
                  classnames={{ wrapper: "xl:max-w-7xl h-full" }}
                  onCancel={closeSidebar}
                  error={errorMessage}
                  disabled={!isValidMaxPeriodDays}
                  formikProps={{
                    onSubmit,
                    initialValues,
                    validationSchema: Yup.object().shape({
                      start_date: Yup.date().when(
                        ["end_date"],
                        (date: Date | undefined, schema: Record<any, any>) => {
                          if (!date || !formRef.current?.values.start_date)
                            return schema;

                          setErrorMessage(null);
                          setIsValidMaxPeriodDays(true);

                          return schema.required("Required");
                        }
                      ),
                      end_date: Yup.date().required("Required"),
                      currency: Yup.object().required("Required"),
                      timezone: Yup.string().required("Required"),
                      koinlyIsDefault: Yup.boolean()
                    }),
                    innerRef: formRef
                  }}
                  orderedFieldNames={ORDERED_FIELD_NAMES}
                  submitButtonText="Generate report"
                  submittingSubmitButtonText="Generating report"
                >
                  {({
                    isSubmitting,
                    values,
                    setFieldValue
                  }: FormikProps<FormValues>) => {
                    return (
                      <div className="flex-1 h-0 overflow-y-auto">
                        <div className="py-6 px-4 bg-hotspotty-700 sm:px-6 dark:bg-gray-800">
                          <div className="flex items-center justify-between">
                            <Dialog.Title className="text-lg font-medium text-white">
                              Export hotspot rewards
                            </Dialog.Title>
                            <div className="ml-3 h-7 flex items-center">
                              <button
                                autoFocus
                                type="button"
                                className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-gray-800 dark:hover:text-white"
                                onClick={closeSidebar}
                              >
                                <span className="sr-only">Close panel</span>
                                <XIcon className="h-6 w-6" aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="flex-1 flex flex-col justify-between">
                          <div className="px-4 divide-y divide-gray-200 dark:divide-gray-500 sm:px-6">
                            <div className="space-y-6 pt-6 pb-5">
                              {blockchainHaltMoment &&
                                values.end_date &&
                                blockchainHaltMoment
                                  .local()
                                  .isBefore(moment(values.end_date)) && (
                                  <AlertMessage
                                    type="warning"
                                    title={`The Blockchain is currently outdated since ${blockchainHaltMoment.fromNow()}. It is advisable to export hotspot rewards later on.`}
                                  />
                                )}

                              <p className="text-sm text-gray-500 dark:text-gray-300">
                                Export all rewards that your hotspots received
                                in HNT over a period of time. It also includes
                                the reward in a secondary currency based on the
                                exchange rate at the time.
                              </p>

                              <Field
                                id="start_date"
                                name="start_date"
                                label="Start date"
                                placeholder={`E.g. ${new Date().toLocaleDateString(
                                  locale
                                )}`}
                                max={moment(now.current).local().toDate()}
                                component={DateInput}
                                disabled={isSubmitting}
                              />

                              <HandleFieldChange<FormValues>
                                name="start_date"
                                onChange={(value) => {
                                  setFieldValue(
                                    "end_date",
                                    moment(values.start_date)
                                      .local()
                                      .add(1, "month")
                                      .toDate()
                                  );
                                }}
                              />

                              <Field
                                id="end_date"
                                name="end_date"
                                label="End date"
                                placeholder={`E.g. ${new Date().toLocaleDateString(
                                  locale
                                )}`}
                                min={moment(values.start_date).local().toDate()}
                                component={DateInput}
                                disabled={
                                  isSubmitting ||
                                  moment(values.start_date)
                                    .local()
                                    .add(1, "days")
                                    .isAfter(moment(now.current).local())
                                }
                              />

                              <Field
                                className="-mt-1"
                                name="currency"
                                label="Secondary currency"
                                disabled
                                items={filter(currencyOptions, {
                                  key: CurrencyKey.usd
                                })}
                                itemToString={(
                                  currencyOption: DropdownSelectOption<CurrencyKey>
                                ) => getCurrencyTitle(currencyOption.key)}
                                optionToString={(
                                  currencyOption: DropdownSelectOption<CurrencyKey>
                                ) => getCurrencyTitle(currencyOption.key)}
                                component={DropdownSelect}
                              />

                              <Field
                                name="timezone"
                                label="Timezone"
                                placeholder="Search a timezone"
                                disabled={isSubmitting}
                                fetchItems={getTimezones}
                                itemToString={(item: string) => timezones[item]}
                                component={DropdownSearch}
                                withoutMinimumSearchCharacters
                                debounceTime={0}
                                initialItems={getTimezones("")}
                                clearInputOnSelect={false}
                              />

                              <Field
                                label={
                                  <div className="inline-flex items-center">
                                    Export rewards compatible with Koinly tax
                                    reporting
                                  </div>
                                }
                                name="koinlyIsDefault"
                                disabled={isSubmitting}
                                type="checkbox"
                                component={Checkbox}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }}
                </FormProvider>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ExportHotspotRewardsSidebar;

//
// Utils
//

interface FormValues {
  start_date: string;
  end_date: string;
  currency: DropdownSelectOption<CurrencyKey>;
  timezone: string;
  koinlyIsDefault: boolean;
}

const ORDERED_FIELD_NAMES = [
  "startDate",
  "endDate",
  "currency",
  "timezone",
  "koinlyIsDefault"
];

export interface ExportHotspotRewardsSidebarConfig {
  show: boolean;
  hotspotIds: string[];
}
